import moment from 'moment'
import { IShipment, TDocumentKeyType } from 'types'
import {
  TFileType,
  ILoad,
  IFileAttachmentWithType,
  IFileCreate,
  IBaseFileType,
} from 'apps/fleet-planner/types'

/**
 * Util function to easily add the right type to a document
 */
export function addFileTypeToDocument<T extends TFileType>(
  document: IFileCreate,
  fileType: T
): IFileAttachmentWithType<T> {
  return {
    ...document,
    fileType,
  }
}

/**
 * Returns array of documents with file
 */
export function addFileTypeToDocumentList<T extends TFileType>(
  documents: IFileCreate[],
  fileType: T
): IFileAttachmentWithType<T>[] {
  return documents.map(d => addFileTypeToDocument(d, fileType))
}

/**
 * Sets all documents to a certain fileType
 */
export function addAttachmentsToLoad<T extends TFileType>(
  load: ILoad,
  documents: IFileCreate[],
  fileType: T
): ILoad {
  const { allAttachments = [] } = load || {}
  const newAttachments = addFileTypeToDocumentList(documents, fileType)
  return {
    ...load,
    allAttachments: [...allAttachments, ...newAttachments],
  }
}

interface IGetMpDocumentsProps {
  documents: IShipment['documents']
  /**
   * Filter by specific types.
   * @note Default value, ['all'] bypasses filter altogether
   * @default ['all']
   */
  fileTypes?: (TDocumentKeyType | 'all')[]
  /**
   * Remove any values with `fileUrl` as null, since `null`
   * is empty state in this context
   * @default true
   */
  removeNull?: boolean
}

/**
 * Returns mapped array of documents
 */
export function getMpDocuments({
  documents = [],
  fileTypes = ['all'],
  removeNull = true,
}: IGetMpDocumentsProps): IBaseFileType[] {
  return (
    documents
      /**
       * If `fileTypes` is set to ['all'], don't filter.
       * Otherwise, only include passed values
       */
      .filter(d => (fileTypes.includes('all') ? true : fileTypes.includes(d.key)))
      /**
       * When `removeNull` is `true`, removes any documents
       * with empty `fileUrl`
       */
      .filter(d => (removeNull ? !!d.fileUrl : true))
      /**
       * Return `IBaseFileType[]` which is widely compatible type
       * for displaying files in Load Details pages
       */
      .map(({ fileName = '', fileUrl: url, key, message: createdAt }) => ({
        fileName,
        url,
        // Marketplace docs use MM/DD/YY for date formats
        createdAt: moment(createdAt ?? undefined, 'MM/DD/YY').format(),
        key,
      }))
  )
}

interface IGetFpDocumentsProps {
  documents: ILoad['attachments'] | ILoad['pods']
}

/**
 * Returns array of documents from FP
 */
export const getFpDocuments = ({ documents = [] }: IGetFpDocumentsProps): IBaseFileType[] => {
  return documents
    .map(doc => {
      const { createdAt, id, url } = doc
      const fileName = doc.fileFileName || doc.filename || ''
      return { id, fileName, url, createdAt }
    })
    .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())
}

interface IGetTotalDocumentsCountProps {
  fpLoad: ILoad
  mpShipment: IShipment
  /**
   * Is the carrier portal integration flag on
   * @default false
   */
  isFpCarrierPortalEnabled?: boolean
}

/**
 * Returns total count of documents based on whether
 * the load is from MP or FP
 */
export const getTotalDocumentsCount = (props: IGetTotalDocumentsCountProps): number => {
  const { fpLoad, isFpCarrierPortalEnabled = false, mpShipment } = props || {}
  const { pods: fpPods = [], attachments: fpAttachments = [], isExternal } = fpLoad || {}
  const { documents: mpDocuments = [] } = mpShipment || {}
  const podsCount = fpPods?.length || 0
  const attachmentsCount = fpAttachments?.length || 0

  /**
   * Use load.attachments.length for both scenarios
   */
  let totalCount = attachmentsCount

  if (isExternal && isFpCarrierPortalEnabled) {
    /**
     * Only add 1 maximum to POD documents, since we only display 1 in UI
     */
    totalCount += podsCount ? 1 : 0

    /**
     * Only add 1 maximum to POD documents, since we only display 1 in UI
     */
    const mpDocumentsCount =
      getMpDocuments({
        documents: mpDocuments,
        fileTypes: ['payable', 'rate_confirmation', 'misc', 'ScaleTicket', 'invoice_file_name'],
      }).length || 0
    totalCount += mpDocumentsCount
  } else {
    totalCount += podsCount
  }

  return totalCount
}
